/**
 * @file ref-keys.ts
 * @module app/modules/service-request/utils/ref-keys
 * @description Splits typed or pasted order reference keys into a clean, de-duplicated list.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

/** Characters that separate refKeys when a user types or pastes a list. */
export const REF_KEY_SEPARATOR = /[\s,;]+/;

/**
 * Splits raw text on commas, semicolons, spaces, or new lines. Keys are trimmed and
 * kept case-sensitive, matching how the backend looks orders up by refKey.
 */
export const splitRefKeys = (raw: string): string[] =>
  raw
    .split(REF_KEY_SEPARATOR)
    .map((refKey) => refKey.trim())
    .filter(Boolean);

/**
 * Adds new keys to an existing list, dropping duplicates and anything past the limit.
 * Returns the merged list plus counts so the input can tell the user what was skipped.
 */
export const mergeRefKeys = (
  current: string[],
  incoming: string[],
  limit: number
): { refKeys: string[]; duplicates: number; overLimit: number } => {
  const seen = new Set(current);
  const refKeys = [...current];
  let duplicates = 0;
  let overLimit = 0;

  incoming.forEach((refKey) => {
    if (seen.has(refKey)) {
      duplicates += 1;
      return;
    }
    if (refKeys.length >= limit) {
      overLimit += 1;
      return;
    }
    seen.add(refKey);
    refKeys.push(refKey);
  });

  return { refKeys, duplicates, overLimit };
};
